import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/apiClient";
import { StatusBadge, PriorityBadge, Avatar, ProgressBar } from "../components/Badges";
import { STATUSES, STATUS_ORDER, PRIORITIES, PRIORITY_ORDER, fmtDate } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";
import { drawLetterhead, drawFooterWave, contentBottom, BRAND_RED_HEX } from "../lib/docTemplate";
import { LOGO_DATA_URL } from "../lib/logoData";
import { ClipboardList, Search, FileText, Download, CheckCircle2, Circle, AlertCircle, CheckCheck, FileType, GripVertical, ArrowUpDown } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

// Estatus incluidos en la consulta (todos, en el orden de STATUS_ORDER)
const QUERY_STATUSES = STATUS_ORDER;

const GROUP_OPTIONS = [
  { value: "status", label: "Estatus" },
  { value: "departamento_solicitante", label: "Departamento solicitante" },
  { value: "solicitante", label: "Solicitante" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const todayDMY = () => new Date().toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
const avanceTextClass = (p) => (p >= 80 ? "text-green-600" : p >= 40 ? "text-amber-500" : "text-red-600");
const avanceRGB = (p) => (p >= 80 ? [22, 163, 74] : p >= 40 ? [202, 138, 4] : [220, 38, 38]);
const avanceHex = (p) => (p >= 80 ? "FF16A34A" : p >= 40 ? "FFCA8A04" : "FFDC2626");
const STATUS_HEX = {
  pendiente: "FF64748B", en_analisis: "FF2563EB", pendiente_info_cliente: "FFD97706",
  en_desarrollo: "FF4F46E5", en_pruebas: "FF9333EA", correccion: "FFEA580C",
  aprobado: "FF059669", listo_deploy: "FF0D9488", produccion_pendiente_firma: "FF16A34A",
  en_produccion: "FF16A34A", capacitacion: "FF0284C7", entregado: "FF65A30D", cancelado: "FF71717A",
};
const statusHex = (s) => STATUS_HEX[s] || "FF334155";
const sisMod = (d) => [d.sistema, d.modulo].filter(Boolean).join(" / ") || "—";
const respNames = (d) => (d.responsables || []).map((r) => r.name).join(", ") || "Sin asignar";

export default function Requirements() {
  const { user } = useAuth();
  const [devs, setDevs] = useState([]);
  const [q, setQ] = useState("");
  const [fSistema, setFSistema] = useState("all");
  const [fResp, setFResp] = useState("all");
  const [fStatuses, setFStatuses] = useState(() => new Set());
  const [groupBy, setGroupBy] = useState("status");
  const [selected, setSelected] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [docOpen, setDocOpen] = useState(false);
  const [docItems, setDocItems] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [docForm, setDocForm] = useState({ solicita: "", ingeniero_analista: "", folio: "000", fecha: todayDMY() });

  useEffect(() => { api.get("/developments").then((r) => setDevs(r.data)); }, []);

  const base = useMemo(() => devs.filter((d) => QUERY_STATUSES.includes(d.status)), [devs]);

  const sistemaOptions = useMemo(() => Array.from(new Set(base.map((d) => d.sistema).filter(Boolean))).sort(), [base]);
  const respOptions = useMemo(() => {
    const map = new Map();
    base.forEach((d) => (d.responsables || []).forEach((r) => r && map.set(r.id, r.name)));
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [base]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return base
      .filter((d) => fStatuses.size === 0 || fStatuses.has(d.status))
      .filter((d) => fSistema === "all" || d.sistema === fSistema)
      .filter((d) => fResp === "all" || (d.responsables || []).some((r) => r.id === fResp))
      .filter((d) => !s || [d.dev_id, d.title, d.description, d.impacto, d.solicitante, d.departamento_solicitante, d.sistema, d.modulo]
        .filter(Boolean).some((x) => String(x).toLowerCase().includes(s)));
  }, [base, q, fSistema, fResp, fStatuses]);

  const toggleStatus = (st) => setFStatuses((prev) => { const n = new Set(prev); n.has(st) ? n.delete(st) : n.add(st); return n; });

  const [sortKey, setSortKey] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const toggleSort = (k) => { if (sortKey === k) setSortAsc((a) => !a); else { setSortKey(k); setSortAsc(true); } };

  const SortTh = ({ k, label, center }) => (
    <th className={`px-3 py-2 ${center ? "text-center" : ""}`}>
      <button type="button" onClick={() => toggleSort(k)} data-testid={`req-sort-${k}`}
        className={`inline-flex items-center gap-1 hover:text-slate-900 transition-colors ${center ? "justify-center" : ""} ${sortKey === k ? "text-slate-900" : ""}`}>
        {label} <ArrowUpDown className={`h-3 w-3 ${sortKey === k ? "opacity-100" : "opacity-40"}`} />
        {sortKey === k && <span className="text-[9px]">{sortAsc ? "▲" : "▼"}</span>}
      </button>
    </th>
  );

  const grouped = useMemo(() => {
    const applySort = (items) => {
      if (!sortKey) return items;
      const arr = [...items];
      arr.sort((a, b) => {
        let va, vb;
        if (sortKey === "priority") { va = PRIORITY_ORDER.indexOf(a.priority); vb = PRIORITY_ORDER.indexOf(b.priority); }
        else if (sortKey === "status") { va = STATUS_ORDER.indexOf(a.status); vb = STATUS_ORDER.indexOf(b.status); }
        else if (sortKey === "sistema") { va = [a.sistema, a.modulo].filter(Boolean).join(" / "); vb = [b.sistema, b.modulo].filter(Boolean).join(" / "); }
        else if (sortKey === "avance") { va = a.avance ?? 0; vb = b.avance ?? 0; }
        else { va = (a[sortKey] || "").toString().toLowerCase(); vb = (b[sortKey] || "").toString().toLowerCase(); }
        if (va < vb) return sortAsc ? -1 : 1;
        if (va > vb) return sortAsc ? 1 : -1;
        return 0;
      });
      return arr;
    };
    if (groupBy === "status") {
      return QUERY_STATUSES
        .map((k) => ({ key: k, label: STATUSES[k].label, status: k, items: applySort(filtered.filter((d) => d.status === k)) }))
        .filter((g) => g.items.length);
    }
    const map = new Map();
    filtered.forEach((d) => {
      const raw = (d[groupBy] || "").trim();
      const key = raw || "__none__";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(d);
    });
    return Array.from(map, ([key, items]) => ({ key, label: key === "__none__" ? "Sin especificar" : key, status: null, items: applySort(items) }))
      .sort((a, b) => (a.key === "__none__" ? 1 : b.key === "__none__" ? -1 : a.label.localeCompare(b.label)));
  }, [filtered, groupBy, sortKey, sortAsc]);

  const showStatusCol = groupBy !== "status";

  const toggleSelect = (id) => setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectGroup = (items, allSel) => setSelectedIds((prev) => { const n = new Set(prev); items.forEach((d) => allSel ? n.delete(d.id) : n.add(d.id)); return n; });
  const selectedCount = useMemo(() => filtered.filter((d) => selectedIds.has(d.id)).length, [filtered, selectedIds]);

  const openDocDialog = async () => {
    setDocItems(filtered.filter((d) => selectedIds.has(d.id)));
    setDocForm((f) => ({ ...f, solicita: f.solicita || user?.name || "", ingeniero_analista: f.ingeniero_analista || user?.name || "", fecha: f.fecha || todayDMY() }));
    setDocOpen(true);
    try { const { data } = await api.get("/requirements/next-folio"); setDocForm((f) => ({ ...f, folio: data.folio })); } catch { /* noop */ }
  };

  const onDocDragEnd = (result) => {
    if (!result.destination) return;
    setDocItems((prev) => {
      const arr = Array.from(prev);
      const [moved] = arr.splice(result.source.index, 1);
      arr.splice(result.destination.index, 0, moved);
      return arr;
    });
  };

  const generateDoc = async () => {
    const ids = docItems.map((d) => d.id);
    if (!ids.length) { toast.error("Selecciona al menos un requerimiento"); return; }
    setGenerating(true);
    try {
      const res = await api.post("/requirements/document", { dev_ids: ids, ...docForm }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `solicitud_requerimientos_${docForm.folio || todayISO()}.docx`; a.click();
      URL.revokeObjectURL(url);
      const { data } = await api.get("/developments");
      setDevs(data);
      setSelectedIds(new Set());
      toast.success(`Documento Word generado (folio ${docForm.folio})`);
      setDocOpen(false);
    } catch (err) {
      toast.error("No se pudo generar el documento");
    } finally {
      setGenerating(false);
    }
  };

  const totalCount = filtered.length;

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const stamp = () => { drawLetterhead(doc, { title: "Consulta de Requerimientos" }); drawFooterWave(doc); };
    const top = drawLetterhead(doc, { title: "Consulta de Requerimientos" });
    drawFooterWave(doc);
    const bottom = contentBottom(doc);
    const margins = { left: 40, right: 40, top, bottom: doc.internal.pageSize.getHeight() - bottom };

    let y = top;
    const cols = ["ID", "Requerimiento", "Descripción", "Impacto", ...(showStatusCol ? ["Estatus"] : []), "Sistema / Módulo", "Solicitante", "Departamento", "F. solicitud", "Responsables", "Prioridad", "Avance", "F. compromiso"];
    const avanceIdx = cols.indexOf("Avance");
    grouped.forEach((g) => {
      if (y > bottom - 60) { doc.addPage(); stamp(); y = top; }
      doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(30);
      doc.text(`${g.label}  (${g.items.length})`, 40, y); y += 6;
      autoTable(doc, {
        startY: y,
        head: [cols],
        body: g.items.map((d) => [
          d.dev_id, d.title, d.description || "", d.impacto || "", ...(showStatusCol ? [STATUSES[d.status]?.label || d.status] : []),
          sisMod(d), d.solicitante || "—", d.departamento_solicitante || "—",
          d.fecha_solicitud ? fmtDate(d.fecha_solicitud) : "—", respNames(d),
          PRIORITIES[d.priority]?.label || d.priority, `${d.avance}%`, d.fecha_compromiso ? fmtDate(d.fecha_compromiso) : "—",
        ]),
        headStyles: { fillColor: [51, 65, 85], textColor: 255, fontStyle: "bold", fontSize: 7 },
        styles: { fontSize: 7, cellPadding: 3, overflow: "linebreak" },
        columnStyles: { 0: { cellWidth: 44 }, [avanceIdx]: { halign: "center", cellWidth: 40 } },
        didParseCell: (data) => {
          if (data.section === "body" && data.column.index === avanceIdx) {
            const p = parseInt(String(data.cell.raw), 10) || 0;
            data.cell.styles.textColor = avanceRGB(p);
            data.cell.styles.fontStyle = "bold";
          }
        },
        didDrawPage: stamp,
        margin: margins,
      });
      y = doc.lastAutoTable.finalY + 16;
    });
    if (grouped.length === 0) { doc.setFontSize(11); doc.setTextColor(120); doc.text("Sin requerimientos que coincidan con el filtro.", 40, top + 20); }
    doc.save(`requerimientos_${todayISO()}.pdf`);
  };

  const exportExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Requerimientos", { views: [{ showGridLines: false }] });

    const headers = ["ID", "Requerimiento", "Descripción", "Impacto", ...(showStatusCol ? ["Estatus"] : []), "Sistema / Módulo", "Solicitante", "Departamento", "F. solicitud", "Responsables", "Prioridad", "Avance", "F. compromiso"];
    const avanceCol = headers.indexOf("Avance") + 1;
    const nCols = headers.length;
    const lastColLetter = String.fromCharCode(64 + nCols);
    const WIDTHS = { "ID": 12, "Requerimiento": 40, "Descripción": 40, "Impacto": 34, "Estatus": 16, "Sistema / Módulo": 26, "Solicitante": 22, "Departamento": 22, "F. solicitud": 14, "Responsables": 30, "Prioridad": 12, "Avance": 10, "F. compromiso": 14 };
    ws.columns = headers.map((h) => ({ width: WIDTHS[h] || 16 }));

    const RED = BRAND_RED_HEX;
    const SLATE = "FF334155";
    const border = { top: { style: "thin", color: { argb: "FFE2E8F0" } }, left: { style: "thin", color: { argb: "FFE2E8F0" } }, bottom: { style: "thin", color: { argb: "FFE2E8F0" } }, right: { style: "thin", color: { argb: "FFE2E8F0" } } };

    const imgId = wb.addImage({ base64: LOGO_DATA_URL.split(",")[1], extension: "png" });
    ws.addImage(imgId, { tl: { col: 0, row: 0 }, ext: { width: 60, height: 46 } });
    ws.mergeCells(`B1:${lastColLetter}1`);
    ws.getCell("B1").value = "Dirección de TI  ·  Consulta de Requerimientos";
    ws.getCell("B1").font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
    ws.getCell("B1").alignment = { vertical: "middle", horizontal: "left", indent: 1 };
    ws.getRow(1).height = 40;
    for (let i = 1; i <= nCols; i++) ws.getCell(1, i).fill = { type: "pattern", pattern: "solid", fgColor: { argb: RED } };
    ws.mergeCells(`B2:${lastColLetter}2`);
    ws.getCell("B2").value = "Comprometidos con la innovación, la eficiencia y el crecimiento.  ·  " + new Date().toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" });
    ws.getCell("B2").font = { italic: true, color: { argb: "FF64748B" }, size: 10 };
    ws.getCell("B2").alignment = { vertical: "middle", horizontal: "left", indent: 1 };
    ws.addRow([]);

    const statusCol = showStatusCol ? headers.indexOf("Estatus") + 1 : 0;
    grouped.forEach((g) => {
      const gr = ws.addRow([`${g.label}  (${g.items.length})`]);
      if (g.status) {
        const stColor = statusHex(g.status);
        gr.getCell(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: stColor } };
        gr.getCell(1).font = { bold: true, color: { argb: "FFFFFFFF" }, size: 12 };
        gr.getCell(1).alignment = { vertical: "middle", horizontal: "center" };
        gr.height = 20;
      } else {
        gr.getCell(1).font = { bold: true, italic: true, color: { argb: SLATE }, size: 12 };
      }
      const hr = ws.addRow(headers);
      hr.eachCell((cell) => { cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: SLATE } }; cell.font = { bold: true, color: { argb: "FFFFFFFF" } }; cell.border = border; });
      g.items.forEach((d) => {
        const row = ws.addRow([
          d.dev_id, d.title, d.description || "", d.impacto || "", ...(showStatusCol ? [STATUSES[d.status]?.label || d.status] : []),
          sisMod(d), d.solicitante || "—", d.departamento_solicitante || "—",
          d.fecha_solicitud ? fmtDate(d.fecha_solicitud) : "—", respNames(d),
          PRIORITIES[d.priority]?.label || d.priority, `${d.avance}%`, d.fecha_compromiso ? fmtDate(d.fecha_compromiso) : "—",
        ]);
        row.eachCell((cell) => { cell.border = border; });
        const av = row.getCell(avanceCol);
        av.alignment = { horizontal: "center" };
        av.font = { bold: true, color: { argb: avanceHex(d.avance) } };
        if (statusCol) {
          const st = row.getCell(statusCol);
          st.font = { bold: true, color: { argb: statusHex(d.status) } };
        }
      });
      ws.addRow([]);
    });

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `requerimientos_${todayISO()}.xlsx`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="bg-white border-b border-border px-8 py-6 flex items-end justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Consulta</p>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-0.5 flex items-center gap-2">
            <ClipboardList className="h-7 w-7" /> Consulta de Requerimientos
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-sm h-9 border-slate-300" onClick={exportExcel} data-testid="req-export-excel-btn"><Download className="h-4 w-4 mr-1.5" />Excel</Button>
          <Button variant="outline" className="rounded-sm h-9 border-slate-300" onClick={exportPDF} data-testid="req-export-pdf-btn"><FileText className="h-4 w-4 mr-1.5" />PDF</Button>
          <Button className="rounded-sm h-9 bg-blue-700 hover:bg-blue-800 text-white disabled:opacity-50" onClick={openDocDialog} disabled={selectedCount === 0} data-testid="req-generate-word-btn"><FileType className="h-4 w-4 mr-1.5" />Generar Word{selectedCount > 0 ? ` (${selectedCount})` : ""}</Button>
        </div>
      </div>

      <div className="p-8 space-y-6">
        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por ID, título, solicitante..." className="rounded-sm h-9 w-72 pl-8" data-testid="req-search" />
          </div>
          <Select value={fSistema} onValueChange={setFSistema}>
            <SelectTrigger data-testid="req-filter-sistema" className="rounded-sm w-52 h-9"><SelectValue placeholder="Sistema" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los sistemas</SelectItem>
              {sistemaOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="rounded-sm h-9 border-slate-300 justify-between w-52 font-normal" data-testid="req-filter-status">
                <span className="truncate">{fStatuses.size === 0 ? "Todos los estatus" : `${fStatuses.size} estatus`}</span>
                <ClipboardList className="h-4 w-4 ml-2 text-slate-400 shrink-0" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-2 rounded-sm" align="start">
              <div className="flex items-center justify-between px-1 pb-2 mb-1 border-b border-slate-100">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Filtrar por estatus</span>
                {fStatuses.size > 0 && <button className="text-[11px] text-blue-600 hover:underline" onClick={() => setFStatuses(new Set())} data-testid="req-filter-status-clear">Limpiar</button>}
              </div>
              <div className="max-h-64 overflow-y-auto space-y-0.5">
                {QUERY_STATUSES.map((st) => (
                  <label key={st} className="flex items-center gap-2 px-1.5 py-1.5 rounded-sm hover:bg-slate-50 cursor-pointer" data-testid={`req-filter-status-${st}`}>
                    <Checkbox checked={fStatuses.has(st)} onCheckedChange={() => toggleStatus(st)} />
                    <span className="text-sm text-slate-700">{STATUSES[st].label}</span>
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <Select value={fResp} onValueChange={setFResp}>
            <SelectTrigger data-testid="req-filter-resp" className="rounded-sm w-52 h-9"><SelectValue placeholder="Responsable" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los responsables</SelectItem>
              {respOptions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider text-slate-400">Agrupar por</span>
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger data-testid="req-group-by" className="rounded-sm w-56 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {GROUP_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <span className="ml-auto font-mono text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-sm" data-testid="req-total">{totalCount} requerimiento(s)</span>
        </div>

        {/* Listado agrupado por estatus */}
        {grouped.map((g) => (
          <div key={g.key} data-testid={`req-group-${g.key}`}>
            <div className="flex items-center gap-2 mb-2">
              {g.status ? <StatusBadge status={g.status} /> : <span className="inline-flex items-center rounded-sm border border-slate-300 bg-slate-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-slate-700">{g.label}</span>}
              <span className="font-mono text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-sm">{g.items.length}</span>
            </div>
            <div className="border border-border bg-white rounded-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[1400px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-border text-left text-[11px] font-bold uppercase tracking-wide text-slate-600">
                    <th className="px-3 py-2 w-8">
                      <Checkbox
                        checked={g.items.every((d) => selectedIds.has(d.id))}
                        onCheckedChange={() => toggleSelectGroup(g.items, g.items.every((d) => selectedIds.has(d.id)))}
                        data-testid={`req-select-all-${g.key}`}
                      />
                    </th>
                    <SortTh k="dev_id" label="ID" />
                    <SortTh k="title" label="Requerimiento" />
                    <SortTh k="description" label="Descripción" />
                    <SortTh k="impacto" label="Impacto" />
                    {showStatusCol && <SortTh k="status" label="Estatus" />}
                    <SortTh k="sistema" label="Sistema / Módulo" />
                    <SortTh k="solicitante" label="Solicitante" />
                    <SortTh k="departamento_solicitante" label="Departamento" />
                    <SortTh k="fecha_solicitud" label="F. solicitud" />
                    <th className="px-3 py-2">Responsables</th>
                    <SortTh k="priority" label="Prioridad" />
                    <SortTh k="avance" label="Avance" center />
                    <SortTh k="fecha_compromiso" label="F. compromiso" />
                  </tr>
                </thead>
                <tbody>
                  {g.items.map((d) => (
                    <tr key={d.id} data-testid={`req-row-${d.dev_id}`} onClick={() => setSelected(d)} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer">
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <Checkbox checked={selectedIds.has(d.id)} onCheckedChange={() => toggleSelect(d.id)} data-testid={`req-select-${d.dev_id}`} />
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-slate-500 whitespace-nowrap">{d.dev_id}</td>
                      <td className="px-3 py-2 font-medium max-w-[280px]">{d.title}</td>
                      <td className="px-3 py-2 text-slate-600 max-w-[260px] truncate" title={d.description || ""}>{d.description || "—"}</td>
                      <td className="px-3 py-2 text-slate-600 max-w-[260px] truncate" title={d.impacto || ""}>{d.impacto || "—"}</td>
                      {showStatusCol && <td className="px-3 py-2"><StatusBadge status={d.status} /></td>}
                      <td className="px-3 py-2 text-slate-600">{sisMod(d)}</td>
                      <td className="px-3 py-2 text-slate-600">{d.solicitante || "—"}</td>
                      <td className="px-3 py-2 text-slate-600">{d.departamento_solicitante || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500 whitespace-nowrap">{d.fecha_solicitud ? fmtDate(d.fecha_solicitud) : "—"}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          {(d.responsables || []).slice(0, 4).map((r) => <Avatar key={r.id} user={r} size={22} />)}
                          {(!d.responsables || d.responsables.length === 0) && <span className="text-slate-400 text-xs">Sin asignar</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2"><PriorityBadge priority={d.priority} /></td>
                      <td className={`px-3 py-2 text-center font-mono font-bold ${avanceTextClass(d.avance)}`}>{d.avance}%</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500 whitespace-nowrap">{d.fecha_compromiso ? fmtDate(d.fecha_compromiso) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        {grouped.length === 0 && (
          <div className="text-center py-16 text-slate-400 border border-dashed border-slate-200 rounded-sm">
            No hay requerimientos que coincidan con el filtro.
          </div>
        )}
      </div>

      <RequirementDetail dev={selected} onClose={() => setSelected(null)} />

      <Dialog open={docOpen} onOpenChange={(o) => !generating && setDocOpen(o)}>
        <DialogContent className="rounded-sm max-w-md max-h-[90vh] overflow-y-auto" data-testid="req-doc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileType className="h-5 w-5" /> Generar documento Word</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">{docItems.length} requerimiento(s) seleccionado(s). Arrastra para definir el orden en que saldrán en el Word.</p>

          <div className="mt-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Orden de los requerimientos</Label>
            <DragDropContext onDragEnd={onDocDragEnd}>
              <Droppable droppableId="doc-items">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps} className="mt-1.5 space-y-1.5 max-h-52 overflow-y-auto" data-testid="doc-order-list">
                    {docItems.map((d, index) => (
                      <Draggable key={d.id} draggableId={d.id} index={index}>
                        {(prov, snapshot) => (
                          <div ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}
                            data-testid={`doc-order-item-${d.dev_id}`}
                            className={`flex items-center gap-2 border rounded-sm px-2.5 py-2 bg-white ${snapshot.isDragging ? "border-blue-400 shadow-md" : "border-slate-200"}`}>
                            <GripVertical className="h-4 w-4 text-slate-400 shrink-0" />
                            <span className="font-mono text-[10px] bg-slate-100 text-slate-500 rounded-sm px-1.5 py-0.5 shrink-0">{index + 1}</span>
                            <span className="font-mono text-[11px] text-slate-400 shrink-0">{d.dev_id}</span>
                            <span className="text-sm truncate">{d.title}</span>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>

          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Solicita</Label>
              <Input data-testid="doc-solicita" value={docForm.solicita} onChange={(e) => setDocForm((f) => ({ ...f, solicita: e.target.value }))} className="mt-1.5 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Ingeniero Analista</Label>
              <Input data-testid="doc-analista" value={docForm.ingeniero_analista} onChange={(e) => setDocForm((f) => ({ ...f, ingeniero_analista: e.target.value }))} className="mt-1.5 rounded-sm" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Folio (automático)</Label>
                <Input data-testid="doc-folio" value={docForm.folio} readOnly className="mt-1.5 rounded-sm bg-slate-50 text-slate-600" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Fecha de solicitud</Label>
                <Input data-testid="doc-fecha" value={docForm.fecha} onChange={(e) => setDocForm((f) => ({ ...f, fecha: e.target.value }))} className="mt-1.5 rounded-sm" placeholder="dd/mm/aaaa" />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" className="rounded-sm" onClick={() => setDocOpen(false)} disabled={generating}>Cancelar</Button>
            <Button className="rounded-sm bg-blue-700 hover:bg-blue-800 text-white" onClick={generateDoc} disabled={generating} data-testid="doc-generate-confirm">
              {generating ? "Generando..." : "Descargar Word"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetaItem({ label, children }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{label}</p>
      <div className="text-sm text-slate-700">{children}</div>
    </div>
  );
}

function RequirementDetail({ dev, onClose }) {
  if (!dev) return null;
  const subs = dev.subtareas || [];
  const subsDone = subs.filter((s) => s.completada).length;
  const corr = dev.correcciones || [];
  return (
    <Dialog open={!!dev} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="req-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-slate-400">{dev.dev_id}</span>
            <StatusBadge status={dev.status} />
            <PriorityBadge priority={dev.priority} />
          </div>
          <DialogTitle className="text-xl mt-1">{dev.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Meta */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <MetaItem label="Folio">{dev.folio || "—"}</MetaItem>
            <MetaItem label="Solicitudes (folios)">{(dev.solicitud_folios || []).length ? dev.solicitud_folios.join(", ") : "—"}</MetaItem>
            <MetaItem label="Sistema / Módulo">{sisMod(dev)}{dev.submodulo ? ` / ${dev.submodulo}` : ""}</MetaItem>
            <MetaItem label="Solicitante">{dev.solicitante || "—"}</MetaItem>
            <MetaItem label="Departamento">{dev.departamento_solicitante || "—"}</MetaItem>
            <MetaItem label="Fecha de solicitud">{dev.fecha_solicitud ? fmtDate(dev.fecha_solicitud) : "—"}</MetaItem>
            <MetaItem label="Fecha compromiso">{dev.fecha_compromiso ? fmtDate(dev.fecha_compromiso) : "—"}</MetaItem>
            <MetaItem label="Responsables">{respNames(dev)}</MetaItem>
          </div>

          {/* Avance */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Avance</p>
              <span className={`font-mono text-sm font-bold ${avanceTextClass(dev.avance)}`}>{dev.avance}%</span>
            </div>
            <ProgressBar value={dev.avance} color={dev.avance >= 80 ? "#16A34A" : dev.avance >= 40 ? "#CA8A04" : "#DC2626"} height={8} />
          </div>

          {/* Descripción */}
          {dev.description && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Descripción</p>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{dev.description}</p>
            </div>
          )}

          {/* Impacto */}
          {dev.impacto && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Impacto</p>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{dev.impacto}</p>
            </div>
          )}

          {/* Subtareas */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-2">
              <CheckCheck className="h-3.5 w-3.5" /> Subtareas
              {subs.length > 0 && <span className="font-mono text-slate-500">{subsDone}/{subs.length}</span>}
            </p>
            {subs.length === 0 ? (
              <p className="text-sm text-slate-400">Sin subtareas.</p>
            ) : (
              <div className="space-y-1.5">
                {subs.map((s) => (
                  <div key={s.id} className="flex items-start gap-2 border border-slate-100 rounded-sm px-3 py-2" data-testid={`req-detail-sub-${s.id}`}>
                    {s.completada ? <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0 mt-0.5" /> : <Circle className="h-4 w-4 text-slate-300 shrink-0 mt-0.5" />}
                    <div className="min-w-0">
                      <div className={`text-sm ${s.completada ? "line-through text-slate-400" : "text-slate-700"}`}>{s.titulo}</div>
                      {s.descripcion && <div className="text-xs text-slate-400">{s.descripcion}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Correcciones */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-2">
              <AlertCircle className="h-3.5 w-3.5" /> Correcciones
              {corr.length > 0 && <span className="font-mono text-slate-500">{corr.filter((c) => c.estado === "pendiente").length} pendiente(s)</span>}
            </p>
            {corr.length === 0 ? (
              <p className="text-sm text-slate-400">Sin correcciones.</p>
            ) : (
              <div className="space-y-1.5">
                {corr.map((c) => (
                  <div key={c.id} className="flex items-start gap-2 border border-slate-100 rounded-sm px-3 py-2" data-testid={`req-detail-corr-${c.id}`}>
                    <span className={`inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[10px] font-bold uppercase shrink-0 ${c.estado === "resuelto" ? "bg-green-50 text-green-700 border-green-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{c.estado}</span>
                    <div className="min-w-0">
                      <div className="text-sm text-slate-700">{c.texto}</div>
                      <div className="text-xs text-slate-400">{c.reportado_por_nombre || "—"} · {c.created_at ? fmtDate(c.created_at) : ""}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
